// angular imports
import { computed, Injectable, OnDestroy,  signal } from "@angular/core";

// local imports
import { NotificationDismissMessage } from "@app/app-common";

import {
  AppDataHttpService,
} from "@core/data/http";

import {
  PackageExportStateHandler, ServicesMatrixExportStateHandler, //PackageExport/ServiceMatrix should not be an app level thing.
  TenantStateHandler, CurrentUserStateHandler,
} from "@app/core/state-handler";

import {
  AppManagementModel,
  TenantModel, 
  CurrentUserModel,   
  AppAvailability, 
  NotificationState, 
} from "@app/core/data/models";

import { ObservableProperty, ObservablePropertyStatus } from "@app/shared/data/models";

import { 
  NotificationFactory,
} from "@app/core/services/notification.factory";

import {  
  AppDataChangedMessage,
  NotificationsStateChangedMessage,
  TenantStateChangedMessage,
  UserLoggedInMessage, UserLoggedOutMessage
} from "@app/core/messages";

import { Messenger } from "@app/shared/data/messaging"

@Injectable({
  providedIn: "root",
})
export class AppStateService implements OnDestroy {
  private _initialized = false;
  private _bootstrapPromise?: Promise<void>;
  private _notificationDismissSubscribed = false;
  private _userLoggedInSubscribed = false;

  private readonly _bootstrapStatus = signal<ObservablePropertyStatus>("idle");
  private readonly _bootstrapError = signal<unknown | undefined>(undefined);

  public readonly bootstrapStatus = computed(() => this._bootstrapStatus());
  public readonly isReady = computed(() => this._bootstrapStatus() === "ready");

  public AppManagementState: AppManagementModel = new AppManagementModel();
  public NotificationState: NotificationState = new NotificationState();
  public CurrentUser = signal<CurrentUserModel | null>(null);
  
  public Tenants = new ObservableProperty<TenantModel[]>();

  private _servicesMatrixExportStateHandler: ServicesMatrixExportStateHandler;
  private _packageExportStateHandler: PackageExportStateHandler;
  private _tenantStateHandler: TenantStateHandler;
  private _currentUserStateHandler: CurrentUserStateHandler;

  constructor(
    private startupHttpService: AppDataHttpService,
    private messenger: Messenger,
    private notificationFactory: NotificationFactory
  ) {
    this._servicesMatrixExportStateHandler = new ServicesMatrixExportStateHandler();
    this._packageExportStateHandler = new PackageExportStateHandler();
    this._tenantStateHandler = new TenantStateHandler();
    this._currentUserStateHandler = new CurrentUserStateHandler();
  }

  public initialize() {
    if (this._initialized) {
      console.warn(`AppStateService: Already initialized, skipping re-initialization`);
      return;
    }

    console.debug(`AppStateService: Initializing`);
    this._initialized = true;

    //TODO:DIW: Shouldn't be servicematrix/package export in platform.
    this._servicesMatrixExportStateHandler.initialize(this, this.messenger);
    this._packageExportStateHandler.initialize(this, this.messenger);
    this._tenantStateHandler.initialize(this, this.messenger);
    this._currentUserStateHandler.initialize(this, this.messenger, this.startupHttpService);
    this.initializeUserLoggedInHandling();
    this.initializeNotificationDismissHandling();
  }

  public async initializeAfterLogin(): Promise<void> {
    this.initialize();

    if (this._bootstrapStatus() === "ready") {
      return;
    }

    if (this._bootstrapPromise) {
      return this._bootstrapPromise;
    }

    this._bootstrapStatus.set("loading");
    this._bootstrapError.set(undefined);

    this._bootstrapPromise = Promise.all([
      this.initializeApplications(),
      this.initializeUserNotifications(),
      this.initializeTenants(),
    ]).then(() => {
      this._bootstrapStatus.set("ready");
    }).catch((error) => {
      this._bootstrapStatus.set("error");
      this._bootstrapError.set(error);
      throw error;
    }).finally(() => {
      this._bootstrapPromise = undefined;
    });

    return this._bootstrapPromise;
  }

  public async waitUntilReady(): Promise<void> {
    this.initialize();

    if (this._bootstrapStatus() === "ready") {
      return;
    }

    if (this._bootstrapPromise) {
      await this._bootstrapPromise;
      return;
    }

    if (this._bootstrapStatus() === "idle") {
      await this.initializeAfterLogin();
      return;
    }

    if (this._bootstrapStatus() === "error") {
      throw this._bootstrapError();
    }
  }

  private async initializeApplications(): Promise<void> {
    if (this.AppManagementState.apps()) {
      return;
    }

    const appsHttpResult = this.startupHttpService.getApps();
    const appResponse = await appsHttpResult.response;
    this.setApplications(AppAvailability.fromDto(appResponse));
  }

  private async initializeUserNotifications(): Promise<void> {
    const userNotificationResults = this.startupHttpService.getUserNotifications();
    const userNotificationResponse = await userNotificationResults.response;

    this.setNotifications(
      userNotificationResponse.notifications
        .map((dto) => this.notificationFactory.createNotification(dto).getMessage())
        .reverse(),
    );
  }

  private async initializeTenants(): Promise<void> {
    if (this.Tenants.status() === 'ready' && this.Tenants.value.length > 0) {
      return;
    }

    try {

      this.Tenants.setStatus('loading');
      this.messenger.send(new TenantStateChangedMessage(this.Tenants));        
          
      const tenantsHttpResult = this.startupHttpService.getTenants();
      const tenantsResponse = await tenantsHttpResult.response;
      
      this.Tenants.setValue(TenantModel.fromDtos(tenantsResponse?.tenants));
      this.Tenants.setStatus('ready');

    } catch (error) {
      // Tenant switching is additive and should not break authenticated startup.
      console.error('AppStateService: Failed to initialize tenant candidates', error);
      this.Tenants.setStatus('error');      
    }
  }

  private initializeNotificationDismissHandling(): void {
    if (this._notificationDismissSubscribed) {
      return;
    }

    this.messenger.subscribe(this, NotificationDismissMessage, async (message) => 
    {
      
      try {
        await this.startupHttpService.deleteNotification(message.notificationId).response;
        this.removeNotification(message.notificationId);
      }
      catch(err) {      
        console.error("Failed to delete notification:", err);
      }
      
    });

    this._notificationDismissSubscribed = true;
  }

  private initializeUserLoggedInHandling(): void {
    if (this._userLoggedInSubscribed) {
      return;
    }

    this.messenger.subscribe(this, UserLoggedInMessage, async _ => {            
      try {
        await this.initializeAfterLogin();
      }
      catch (err) {            
        console.error("Failed to initialize app state after login", err);
        // Dispatch forced logout to drop them back to login failed and exit the loop
        this.messenger.send(new UserLoggedOutMessage(true));
      };

    });

    this._userLoggedInSubscribed = true;
  }

  public setApplications(appAvailability: AppAvailability) {
    this.AppManagementState.apps.set(appAvailability);
    this.messenger.send(new AppDataChangedMessage(this.AppManagementState));
  }

  public setNotifications(notifications: any[]) {
    this.NotificationState.notifications.set(notifications);
    this.messenger.send(new NotificationsStateChangedMessage(this.NotificationState));
  }

  public addNotification(notification: any) {
    this.NotificationState.notifications.update((notifications) => [...notifications, notification]);
    this.messenger.send(new NotificationsStateChangedMessage(this.NotificationState));
  }

  public removeNotification(notificationId: string) {
    this.NotificationState.notifications.update((notifications) =>
      notifications.filter((notification) => notification.id !== notificationId)
    );
    this.messenger.send(new NotificationsStateChangedMessage(this.NotificationState));
  }

  ngOnDestroy(): void {
    this.messenger.unsubscribeByOwner(this);
    this._initialized = false;
    this._bootstrapPromise = undefined;
    this._bootstrapStatus.set("idle");
    this._bootstrapError.set(undefined);
    this._notificationDismissSubscribed = false;
    this._userLoggedInSubscribed = false;
  }
}
